// Singleton WebSocket
let socketInstance = null;
let messageQueue = [];
let reconnectAttempts = 0;
let reconnectTimeout = null;
let subscribers = {};
let isConnected = false;
let authLogoutCallback = null;

// Dodaj flagę dla trwającego wylogowania
let isLoggingOut = false;

// Maksymalna liczba prób ponownego połączenia
const MAX_RECONNECT_ATTEMPTS = 10;

// Dodaj zmienną DEBUG_WS na górze pliku
const DEBUG_WS = process.env.NODE_ENV === 'development';

const WebSocketService = {
  initialize(authLogoutHandler) {
    // Dodaj walidację funkcji wylogowania
    if (typeof authLogoutHandler !== 'function') {
      console.error("WebSocketService: authLogoutHandler nie jest funkcją!");
      return;
    }
    
    authLogoutCallback = authLogoutHandler;
  },

  connect() {
    // Nie twórz nowego połączenia jeśli już istnieje
    if (socketInstance && (socketInstance.readyState === WebSocket.OPEN || 
        socketInstance.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket jest już otwarty lub w trakcie łączenia. Pomijam.");
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      console.error("Brak tokenu dostępu. Połączenie WebSocket nie zostało nawiązane.");
      return;
    }

    const wsUrl = `${process.env.REACT_APP_WEBSOCKET_URL}?token=${token}`;
    console.log("Łączę z WebSocket:", wsUrl);
    
    try {
      socketInstance = new WebSocket(wsUrl);
      
      socketInstance.onopen = () => {
        console.log("Połączenie WebSocket nawiązane.");
        isConnected = true;
        reconnectAttempts = 0;

        // Przetwórz kolejkę wiadomości
        while (messageQueue.length > 0) {
          const message = messageQueue.shift();
          this.send(message);
        }
      };

      socketInstance.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (DEBUG_WS) console.log("[WebSocketService] Otrzymano wiadomość:", data);
          
          // Sprawdź, czy wiadomość zawiera error o wygaśnięciu tokenu
          if (data.error && data.error.includes('Token expired')) {
            console.error("Otrzymano wiadomość o wygaśnięciu tokenu");
            this._handleTokenExpiration();
            return;
          }
          
          // Obsługa różnych typów wiadomości
          this._handleMessage(data);
        } catch (error) {
          console.error("Błąd podczas przetwarzania wiadomości:", error);
        }
      };

      socketInstance.onclose = (event) => {
        console.log(`WebSocket zamknięty z kodem: ${event.code}`);
        isConnected = false;
        
        if (event.code === 1000) {
          console.log("WebSocket zamknięty normalnie z kodem 1000.");
        } else if (event.code === 4003) {
          console.error("WebSocket zamknięty z powodu wygaśnięcia tokenu. Wylogowuję.");
          this._handleTokenExpiration();
        } else {
          console.error(`WebSocket zamknięty niespodziewanie z kodem: ${event.code}`);
          
          // Logika ponownego połączenia
          if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            const token = localStorage.getItem("access_token");
            if (token) {
              const timeout = Math.min(1000 * (2 ** reconnectAttempts), 30000);
              console.log(`Próba ponownego połączenia za ${timeout/1000} sekund...`);
              
              if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
              }
              
              reconnectTimeout = setTimeout(() => {
                reconnectAttempts += 1;
                console.log(`Próba ponownego połączenia ${reconnectAttempts}`);
                this.connect();
              }, timeout);
            }
          } else {
            console.error(`Przekroczono maksymalną liczbę prób (${MAX_RECONNECT_ATTEMPTS}). Przechodzę na polling.`);
            // Tutaj możesz zaimplementować logikę fallback na polling
          }
        }
      };

      socketInstance.onerror = (error) => {
        console.error("Błąd WebSocket:", error);
      };
    } catch (error) {
      console.error("Błąd podczas tworzenia połączenia WebSocket:", error);
    }
  },
  
  // Nowa metoda do obsługi wygaśnięcia tokenu
  _handleTokenExpiration() {
    console.log("Obsługa wygaśnięcia tokenu...");
    
    // Sprawdź flagę - jeśli wylogowanie już trwa, przerwij funkcję
    if (isLoggingOut) {
      console.log("Wylogowanie już w toku, ignoruję duplikat wywołania");
      return;
    }
    
    // Ustaw flagę na true - rozpoczynamy wylogowanie
    isLoggingOut = true;
    
    // Rozłącz WebSocket przed wylogowaniem
    this.disconnect();
    
    // Wywołaj callback wylogowania
    if (!authLogoutCallback) {
      console.error("Brak funkcji wylogowania!");
      // Awaryjne czyszczenie lokalnych danych
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Przekieruj na stronę logowania
      window.location.href = '/login';
      return;
    }
    
    // Wywołaj callback wylogowania
    console.log("Wywołuję funkcję wylogowania...");
    setTimeout(() => {
      try {
        authLogoutCallback();
        // Resetuj flagę po 2 sekundach
        setTimeout(() => {
          isLoggingOut = false;
        }, 2000);
      } catch (error) {
        console.error("Błąd podczas wylogowania:", error);
        isLoggingOut = false;
        // Awaryjne przekierowanie
        window.location.href = '/login';
      }
    }, 0);
  },

  disconnect() {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (socketInstance && typeof socketInstance.close === 'function') {
      try {
        if (socketInstance.readyState === WebSocket.OPEN) {
          socketInstance.close(1000, "Rozłączony przez klienta");
          console.log("Połączenie WebSocket zamknięte.");
        }
      } catch (error) {
        console.error("Błąd podczas zamykania WebSocket:", error);
      }
    }
    
    isConnected = false;
    socketInstance = null;
  },

  send(message) {
    if (socketInstance && socketInstance.readyState === WebSocket.OPEN) {
      socketInstance.send(JSON.stringify(message));
      console.log("Wysłano wiadomość:", message);
      return true;
    } else {
      console.log("WebSocket nie jest otwarty. Dodaję wiadomość do kolejki:", message);
      messageQueue.push(message);
      return false;
    }
  },
  
  _handleMessage(data) {
    this._notifySubscribers(data);
  },
  
  _notifySubscribers(data) {
    const messageType = data.type;
    
    if (subscribers[messageType]) {
      subscribers[messageType].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Błąd w subskrybencie dla ${messageType}:`, error);
        }
      });
    }
    
    if (subscribers["*"]) {
      subscribers["*"].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error("Błąd w ogólnym subskrybencie:", error);
        }
      });
    }
  },

  subscribe(messageType, callback) {
    if (typeof messageType === "function" && !callback) {
      callback = messageType;
      messageType = "*";
    }
    
    if (!subscribers[messageType]) {
      subscribers[messageType] = [];
    }
    
    subscribers[messageType].push(callback);
    
    return () => {
      this.unsubscribe(messageType, callback);
    };
  },
  
  unsubscribe(messageType, callback) {
    if (typeof messageType === "function" && !callback) {
      callback = messageType;
      messageType = "*";
    }
    
    if (subscribers[messageType]) {
      subscribers[messageType] = subscribers[messageType].filter(cb => cb !== callback);
      
      if (subscribers[messageType].length === 0) {
        delete subscribers[messageType];
      }
    }
  },
  
  isConnected() {
    return isConnected;
  },
  
  getConnectionState() {
    if (!socketInstance) return "CLOSED";
    
    switch (socketInstance.readyState) {
      case WebSocket.CONNECTING:
        return "CONNECTING";
      case WebSocket.OPEN:
        return "OPEN";
      case WebSocket.CLOSING:
        return "CLOSING";
      case WebSocket.CLOSED:
        return "CLOSED";
      default:
        return "UNKNOWN";
    }
  },

  getSubscriptions() {
    // Zwróć kopię listy subskrybentów
    return {...subscribers};
  }
};

export default WebSocketService;